import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import type { AppEnvironment, PermissionGroup } from 'src/api/facade/types'
import {
  applicationsApplicationsCredentialsCreateMutation,
  applicationsApplicationsCredentialsListOptions,
  applicationsApplicationsCredentialsListQueryKey,
  applicationsApplicationsCredentialsPartialUpdateMutation,
  applicationsApplicationsCredentialsRetrieveQueryKey,
  applicationsApplicationsCredentialsRevokeCreateMutation,
  applicationsApplicationsCredentialsRotateCreateMutation
} from 'src/api/main'
// eslint-disable-next-line no-restricted-imports -- P2 (86caeu8xd): app roles & custom permissions still on mock until the app-RBAC contract lands
import { mockAppApi, mockQueryKeys } from 'src/api/mock'

import { invalidateWorkspaceApplicationsListQueries } from '../utils/invalidateWorkspaceApplicationQueries'

export const useWorkspaceAppDetail = (
  workspaceId: number,
  appId: string | undefined
) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const enabled = Boolean(appId)
  const id = appId ?? ''
  const applicationIdNumeric = Number(id) || 0

  const { data: roles = [], isLoading: isLoadingRoles } = useQuery({
    queryKey: mockQueryKeys.appRoles(id),
    enabled,
    queryFn: () => mockAppApi.listAppRoles(id)
  })

  const {
    data: appCustomPermissions = [],
    isLoading: isLoadingAppCustomPermissions
  } = useQuery({
    queryKey: mockQueryKeys.appCustomPermissions(id),
    enabled,
    queryFn: () => mockAppApi.listAppCustomPermissions(id)
  })

  const credentialsListOptions = applicationsApplicationsCredentialsListOptions(
    {
      path: { application_id: applicationIdNumeric }
    }
  )
  const credentialsListQueryKey =
    applicationsApplicationsCredentialsListQueryKey({
      path: { application_id: applicationIdNumeric }
    })

  const { data: credentials = [], isLoading: isLoadingCredentials } = useQuery({
    ...credentialsListOptions,
    enabled: enabled && applicationIdNumeric > 0
  })

  const invalidateCredentials = (credentialId?: string) => {
    void queryClient.invalidateQueries({ queryKey: credentialsListQueryKey })
    // The detail page reads from the retrieve query — without invalidating
    // it too, edit/rotate/revoke via detail leave the visible card stale.
    if (credentialId) {
      void queryClient.invalidateQueries({
        queryKey: applicationsApplicationsCredentialsRetrieveQueryKey({
          path: { application_id: applicationIdNumeric, id: credentialId }
        })
      })
    }
  }

  const refreshWorkspaceApps = async () => {
    await invalidateWorkspaceApplicationsListQueries(queryClient, workspaceId)
  }

  const refreshAppDetails = async (targetAppId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: mockQueryKeys.appRoles(targetAppId)
      }),
      queryClient.invalidateQueries({
        queryKey: mockQueryKeys.appCustomPermissions(targetAppId)
      }),
      queryClient.invalidateQueries({
        queryKey: mockQueryKeys.workspaceAppRecord(targetAppId)
      }),
      queryClient.invalidateQueries({
        queryKey: mockQueryKeys.workspaceApps(workspaceId)
      }),
      refreshWorkspaceApps()
    ])
  }

  const { mutateAsync: updateApp, isPending: isUpdatingApp } = useMutation({
    mutationFn: (values: {
      appId: string
      name: string
      goal: string
      description: string
      owner: string
      environment: AppEnvironment
      status: 'active' | 'archived'
    }) =>
      mockAppApi.updateWorkspaceApp(values.appId, {
        name: values.name,
        goal: values.goal,
        description: values.description,
        owner: values.owner,
        environment: values.environment,
        status: values.status
      }),
    onSuccess: async (_, values) => {
      message.success(t('workspace.apps.messages.updated'))
      await refreshAppDetails(values.appId)
    }
  })

  const { mutateAsync: patchAppSettings, isPending: isPatchingAppSettings } =
    useMutation({
      mutationFn: (payload: {
        appId: string
        settings: Record<string, unknown>
      }) =>
        mockAppApi.updateWorkspaceApp(payload.appId, {
          settings: payload.settings
        }),
      onSuccess: async (_, payload) => {
        await refreshAppDetails(payload.appId)
      }
    })

  const {
    mutateAsync: createAppPermission,
    isPending: isCreatingAppPermission
  } = useMutation({
    mutationFn: (values: {
      appId: string
      codeName: string
      displayName?: string
      description?: string
    }) => mockAppApi.createAppCustomPermission(values),
    onSuccess: async (_, values) => {
      message.success(t('workspace.apps.messages.customPermissionCreated'))
      await refreshAppDetails(values.appId)
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : ''
      if (msg === 'duplicate_app_permission_code') {
        message.error(t('workspace.apps.roles.appCustomPermissionDuplicate'))
      } else {
        message.error(t('workspace.apps.messages.customPermissionCreateFailed'))
      }
    }
  })

  const {
    mutateAsync: deleteAppPermission,
    isPending: isDeletingAppPermission
  } = useMutation({
    mutationFn: (payload: { appId: string; permissionId: string }) =>
      mockAppApi.deleteAppCustomPermission(payload.appId, payload.permissionId),
    onSuccess: async (_, values) => {
      await refreshAppDetails(values.appId)
    }
  })

  const { mutateAsync: createRole, isPending: isCreatingRole } = useMutation({
    mutationFn: (values: {
      appId: string
      name: string
      description: string
      permissions: PermissionGroup[]
      customPermissionIds: string[]
      source?: 'workspace' | 'custom'
    }) =>
      mockAppApi.createAppRole({
        appId: values.appId,
        name: values.name,
        description: values.description,
        permissions: values.permissions,
        customPermissionIds: values.customPermissionIds,
        source: values.source
      }),
    onSuccess: async (_, values) => {
      await refreshAppDetails(values.appId)
    }
  })

  const { mutateAsync: removeRole, isPending: isRemovingRole } = useMutation({
    mutationFn: (payload: { appId: string; roleId: string }) =>
      mockAppApi.deleteAppRole(payload.appId, payload.roleId),
    onSuccess: async (_, values) => {
      await refreshAppDetails(values.appId)
    }
  })

  const { mutateAsync: updateRole, isPending: isUpdatingRole } = useMutation({
    mutationFn: (values: {
      appId: string
      roleId: string
      name: string
      description: string
      permissions: PermissionGroup[]
      customPermissionIds: string[]
    }) =>
      mockAppApi.updateAppRole(values.appId, values.roleId, {
        name: values.name,
        description: values.description,
        permissions: values.permissions,
        customPermissionIds: values.customPermissionIds
      }),
    onSuccess: async (_, values) => {
      message.success(t('workspace.apps.messages.roleUpdated'))
      await refreshAppDetails(values.appId)
    }
  })

  const { mutateAsync: createCredential, isPending: isCreatingCredential } =
    useMutation({
      ...applicationsApplicationsCredentialsCreateMutation(),
      onSuccess: () => invalidateCredentials()
    })

  const { mutateAsync: rotateCredential, isPending: isRotatingCredential } =
    useMutation({
      ...applicationsApplicationsCredentialsRotateCreateMutation(),
      onSuccess: (_data, variables) => invalidateCredentials(variables.path?.id)
    })

  const { mutateAsync: revokeCredential, isPending: isRevokingCredential } =
    useMutation({
      ...applicationsApplicationsCredentialsRevokeCreateMutation(),
      onSuccess: (_data, variables) => invalidateCredentials(variables.path?.id)
    })

  const { mutateAsync: updateCredential, isPending: isUpdatingCredential } =
    useMutation({
      ...applicationsApplicationsCredentialsPartialUpdateMutation(),
      onSuccess: (_data, variables) => invalidateCredentials(variables.path?.id)
    })

  return {
    roles,
    isLoadingRoles,
    appCustomPermissions,
    isLoadingAppCustomPermissions,
    createAppPermission,
    isCreatingAppPermission,
    deleteAppPermission,
    isDeletingAppPermission,
    credentials,
    isLoadingCredentials,
    updateApp,
    isUpdatingApp,
    patchAppSettings,
    isPatchingAppSettings,
    createRole,
    isCreatingRole,
    removeRole,
    isRemovingRole,
    updateRole,
    isUpdatingRole,
    createCredential,
    isCreatingCredential,
    rotateCredential,
    isRotatingCredential,
    revokeCredential,
    isRevokingCredential,
    updateCredential,
    isUpdatingCredential,
    refreshAppDetails
  }
}
